/**
 * Interface sounds, synthesised with the Web Audio API.
 *
 * No audio files, for the same reason the ambient pad has none: a few
 * oscillators with scheduled envelopes cost a couple of hundred bytes, load
 * instantly, and survive being inlined into a single-file build.
 *
 * The AudioContext is created lazily on first play. That's deliberate — every
 * call site is inside a click handler, so the context is always born inside a
 * user gesture and autoplay policy is satisfied.
 */

let ctx = null;
let master = null;
let muted = false;

/** Mirror of the persisted `settings.sfx` preference. */
export function setMuted(value) {
  muted = !!value;
}

export function isMuted() {
  return muted;
}

function audio() {
  if (ctx) return ctx;
  const AudioCtx = window.AudioContext ?? window.webkitAudioContext;
  if (!AudioCtx) return null;

  ctx = new AudioCtx();
  master = ctx.createGain();
  /*
   * Was 0.22, which measured -14.5 dBFS peak for the correct-answer bell and
   * -26.4 dBFS for the wrong one. "Sits under speech" turned into "nobody can
   * hear it": a 200ms blip that quiet is gone under any room noise, and on a
   * phone at half volume it may as well not fire. This lands the bell near
   * -10 dBFS, which is a normal level for a UI sound and still well short of
   * startling.
   */
  master.gain.value = 0.38;

  // Takes the hard edge off the upper partials so the bell reads warm
  // rather than piercing on phone speakers.
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = 5200;
  tone.Q.value = 0.4;

  master.connect(tone).connect(ctx.destination);
  return ctx;
}

/**
 * One struck bell: a fundamental plus two inharmonic partials, each with its
 * own decay so the tone thins out as it rings, the way a real bell does.
 */
function strike(at, frequency, peak) {
  const partials = [
    { ratio: 1, gain: 1, decay: 1 },
    { ratio: 2.01, gain: 0.38, decay: 0.62 },
    { ratio: 3.02, gain: 0.14, decay: 0.42 },
  ];

  for (const partial of partials) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency * partial.ratio, at);

    const gain = ctx.createGain();
    const decay = 0.85 * partial.decay;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak * partial.gain, at + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + decay);

    osc.connect(gain).connect(master);
    osc.start(at);
    osc.stop(at + decay + 0.05);
  }
}

/**
 * The correct-answer ding: two bells a perfect fifth apart, struck 85ms
 * apart. A rising interval is what makes it read as affirmation rather than
 * as a notification.
 */
export function playCorrect() {
  if (muted || !audio()) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const now = ctx.currentTime + 0.01;
  strike(now, 659.25, 0.5); // E5
  strike(now + 0.085, 987.77, 0.42); // B5
}

/**
 * The wrong-answer sound: a soft falling tone. Deliberately gentle — this is a
 * study app, and a harsh buzzer punishes the exact moment someone is learning
 * something.
 *
 * ── Why it is not as low as it used to be ───────────────────────────────────
 *
 * It was a lone triangle at 196Hz falling to 155Hz, which is a fine sound on
 * headphones and inaudible on a phone. A phone speaker is a few millimetres
 * across and rolls off steeply below roughly 500Hz, so almost none of that
 * reached the air; a triangle's own harmonics fall away as 1/n² and gave it no
 * help. Measured, it was the quietest thing in the app by 12dB.
 *
 * So the fundamental moves up a fourth, and an octave above it is voiced
 * quietly alongside — the pair still reads as one low, soft tone on a decent
 * speaker, while the upper voice is what a phone can actually reproduce.
 */
export function playWrong() {
  if (muted || !audio()) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const now = ctx.currentTime + 0.01;
  // [start, end, peak] — the octave rides at a third the level, enough to carry
  // the sound on a small speaker without turning it into a beep.
  const voices = [
    [262, 208, 0.5], // C4 → G#3
    [524, 416, 0.17],
  ];

  for (const [from, to, peak] of voices) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.exponentialRampToValueAtTime(to, now + 0.22);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);

    osc.connect(gain).connect(master);
    osc.start(now);
    osc.stop(now + 0.4);
  }
}

/** Release the context — used when the app unmounts. */
export function dispose() {
  ctx?.close?.().catch(() => {});
  ctx = null;
  master = null;
}
